/**
 * OfflineStatusBanner.js — single global pill that surfaces:
 *   • offline (red) — "X items pending — will auto-sync when online"
 *   • online + pending (amber) — "Syncing N items…" with retry button
 *   • online + just synced (green flash) — "All caught up"
 *
 * Mounted once at the App root. Uses no router internals so it works
 * on every page including login/landing.
 */
import { useEffect, useState } from 'react';
import { CloudOff, RefreshCw, CheckCircle2, Loader2 } from 'lucide-react';
import useOnlineStatus from '@/hooks/useOnlineStatus';

export default function OfflineStatusBanner() {
  const { online, pendingChunks, pendingMutations, pendingTotal, draining, drainNow, lastSyncedAt } = useOnlineStatus();
  const [showAllCaughtUp, setShowAllCaughtUp] = useState(false);

  // Briefly flash a green "All caught up" pill right after a successful drain.
  useEffect(() => {
    if (!lastSyncedAt) return;
    setShowAllCaughtUp(true);
    const t = setTimeout(() => setShowAllCaughtUp(false), 3500);
    return () => clearTimeout(t);
  }, [lastSyncedAt]);

  // Hide entirely when there's nothing to say.
  if (online && pendingTotal === 0 && !showAllCaughtUp) return null;

  // ── Online + recently synced
  if (online && pendingTotal === 0 && showAllCaughtUp) {
    return (
      <div
        data-testid="offline-banner-synced"
        className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full bg-emerald-600 text-white shadow-lg px-4 py-1.5 text-xs font-semibold animate-in fade-in slide-in-from-top-2"
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        All caught up — local changes synced
      </div>
    );
  }

  // ── Offline
  if (!online) {
    return (
      <div
        data-testid="offline-banner-offline"
        className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full bg-amber-500 text-white shadow-lg px-4 py-1.5 text-xs font-semibold max-w-[92vw]"
      >
        <CloudOff className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">
          You're offline
          {pendingTotal > 0 ? (
            <> — {pendingTotal} item{pendingTotal !== 1 ? 's' : ''} queued, will auto-sync</>
          ) : (
            <> — your edits are saved locally and will sync when you reconnect</>
          )}
        </span>
      </div>
    );
  }

  // ── Online but still draining a backlog
  return (
    <div
      data-testid="offline-banner-syncing"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full bg-blue-600 text-white shadow-lg px-4 py-1.5 text-xs font-semibold max-w-[92vw]"
    >
      {draining ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
      ) : (
        <CloudOff className="w-3.5 h-3.5 shrink-0" />
      )}
      <span className="truncate">
        Syncing {pendingTotal} item{pendingTotal !== 1 ? 's' : ''}
        {pendingChunks > 0 && pendingMutations > 0 ? ` (${pendingChunks} audio + ${pendingMutations} edits)` : ''}
        {pendingChunks > 0 && pendingMutations === 0 ? ' (audio chunks)' : ''}
        {pendingMutations > 0 && pendingChunks === 0 ? ' (edits)' : ''}
      </span>
      <button
        type="button"
        onClick={drainNow}
        disabled={draining}
        data-testid="offline-banner-retry"
        className="ml-1 inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 disabled:opacity-50 px-2 py-0.5 text-[11px] font-bold transition-colors"
      >
        <RefreshCw className={`w-3 h-3 ${draining ? 'animate-spin' : ''}`} />
        Retry
      </button>
    </div>
  );
}
